import {defineMessages} from 'react-intl';
import {map, mergeMap} from '@igp/shared/otel';
import {BaseEpicsBuilder} from '@igp/shared/redux';
import {RealtimeMessageTrigger} from '@igp/shared/redux/realtime';
import {EntityType} from '@models';
import {messageActions, realtimeActions} from '@redux';
import {inject, injectable} from 'inversify';
import type {Epic} from 'redux-observable';
import {filter, of} from 'rxjs';
import {isActionOf} from 'typesafe-actions';

import {debtResponsibleMasterAgentActions} from './actions';
import type {IDebtResponsibleMasterAgentsService} from './service';
import {
    type AddDebtResponsibleMasterPayload,
    DebtResponsibleMasterAgentsServiceTypes,
    type RemoveDebtResponsibleMasterPayload,
} from './types';

export const localized = defineMessages({
    addDebtResponsibleMasterAgentSuccess: {
        id: 'debtResponsibleMasterAgent_addAgentSuccess',
        defaultMessage: 'Debt responsible master agent added',
    },
    addDebtResponsibleMasterAgentFailure: {
        id: 'debtResponsibleMasterAgent_addAgentFailure',
        defaultMessage: 'Debt responsible master agent adding failed',
    },
    removeDebtResponsibleMasterAgentSuccess: {
        id: 'debtResponsibleMasterAgent_removeAgentSuccess',
        defaultMessage: 'Debt responsible master agent removed',
    },
    removeDebtResponsibleMasterAgentFailure: {
        id: 'debtResponsibleMasterAgent_removeAgentFailure',
        defaultMessage: 'Debt responsible master agent removing failed',
    },
});

@injectable()
export class DebtResponsibleMasterAgentEpicsBuilder extends BaseEpicsBuilder {
    constructor(@inject(DebtResponsibleMasterAgentsServiceTypes.service) private readonly _service: IDebtResponsibleMasterAgentsService) {
        super();
    }

    buildEpicList(): Epic[] {
        return [
            this.buildAddAgentRequestEpic(),
            this.buildAddAgentSuccessEpic(),
            this.buildAddAgentFailureEpic(),
            this.buildRemoveAgentEpic(),
            this.buildRemoveAgentSuccessEpic(),
            this.buildRemoveAgentFailureEpic(),
        ];
    }

    private buildAddAgentRequestEpic() {
        return this.buildRequestEpic(debtResponsibleMasterAgentActions.addAgent, (payload: AddDebtResponsibleMasterPayload) =>
            this._service.addDebtResponsibleMasterAgent(payload)
        );
    }

    private buildAddAgentSuccessEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(debtResponsibleMasterAgentActions.addAgent.success)),
                mergeMap(() => {
                    return of(
                        realtimeActions.event({
                            entity: EntityType.DebtResponsibleMasterAgents,
                            items: [],
                            trigger: {type: RealtimeMessageTrigger.Add},
                        }),
                        messageActions.saveMessage({message: localized.addDebtResponsibleMasterAgentSuccess, mode: 'success'})
                    );
                })
            );
    }

    private buildAddAgentFailureEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(debtResponsibleMasterAgentActions.addAgent.failure)),
                map(() => messageActions.saveMessage({message: localized.addDebtResponsibleMasterAgentFailure, mode: 'error'}))
            );
    }

    private buildRemoveAgentEpic() {
        return this.buildRequestEpic(debtResponsibleMasterAgentActions.removeAgent, (payload: RemoveDebtResponsibleMasterPayload) =>
            this._service.removeDebtResponsibleMasterAgent(payload)
        );
    }

    private buildRemoveAgentSuccessEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(debtResponsibleMasterAgentActions.removeAgent.success)),
                mergeMap(() => {
                    return of(
                        realtimeActions.event({
                            entity: EntityType.DebtResponsibleMasterAgents,
                            items: [],
                            trigger: {type: RealtimeMessageTrigger.Delete},
                        }),
                        messageActions.saveMessage({message: localized.removeDebtResponsibleMasterAgentSuccess, mode: 'success'})
                    );
                })
            );
    }

    private buildRemoveAgentFailureEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(debtResponsibleMasterAgentActions.removeAgent.failure)),
                map(() => messageActions.saveMessage({message: localized.removeDebtResponsibleMasterAgentFailure, mode: 'error'}))
            );
    }
}
